/* eslint-disable max-len */

import { FC, useEffect, useState } from 'react';
import { Button, Col, Popconfirm, Row, Space, Spin, Switch } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { FiClock, FiExternalLink, FiUser, FiUserPlus } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { HiOutlineMail } from 'react-icons/hi';
import { BsTelephone } from 'react-icons/bs';
import { HiOutlineIdentification } from 'react-icons/hi2';
import { GoShieldLock } from 'react-icons/go';
import TextCopyable from 'components/TextCopyable';
import EmptyHorizontal from 'components/EmptyHorizontal';
import appRoutes from 'config/appRoutes';
import { parseDateToUSFormat } from 'utils/string.utils';

interface ContactsCardProps {
  contacts?: Record<string, any>[];
  fetching?: boolean;
  clientUuid?: string;
  onInvite: () => void;
  onDeleteContact: (contact: any) => Promise<void>;
  onResendInvite: (contact: any) => void;
  onToggleStrictRequests?: (contact: any, value: boolean) => Promise<void>;
}

const ContactsCard: FC<ContactsCardProps> = ({ contacts, fetching, clientUuid, onInvite, onDeleteContact, onResendInvite, onToggleStrictRequests }) => {
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null);
  const [updatingStrictRequests, setUpdatingStrictRequests] = useState<string | null>(null);
  const [pendingValue, setPendingValue] = useState<{ uuid: string; value: boolean } | null>(null);
  const [localStrictRequests, setLocalStrictRequests] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const map: Record<string, boolean> = {};
    contacts?.forEach((contact) => {
      const customer = contact.customers?.find((c: any) => c.uuid === clientUuid);
      map[contact.uuid] = !!customer?.pivot?.strict_requests;
    });
    setLocalStrictRequests(map);
  }, [contacts, clientUuid]);

  const onDeleteContactHandler = (contact: any) => {
    setDeletingUuid(contact.uuid);

    onDeleteContact(contact).finally(() => {
      setDeletingUuid(null);
    });
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div className="mb-s" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 16, fontWeight: 500 }}>Contacts</span>

        <Button
          type="link"
          size="small"
          className="primary"
          style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 4 }}
          onClick={() => onInvite()}
        >
          <FiUserPlus />
          Invite
        </Button>
      </div>

      <Spin spinning={fetching}>
        <Space direction="vertical" className="w-full">
          {contacts?.map((contact) => (
            <div key={contact.uuid} style={{ border: '1px solid #0044CC33', borderRadius: 4, padding: '8px 12px' }}>
              <Row wrap={false} gutter={[6, 0]} align="middle" className="mb-s">
                <Col flex="none" style={{ width: 85, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FiUser style={{ fontSize: 16 }} />
                  Name:
                </Col>

                <Col flex="auto" className="secondary">
                  <TextCopyable>{contact.name}</TextCopyable>
                </Col>

                <Col flex="none" className="secondary">
                  <Link to={`${appRoutes.CONTACTS}/${contact.uuid}/edit`}>
                    <FiExternalLink />
                  </Link>
                </Col>
              </Row>

              <Row wrap={false} gutter={[6, 0]} align="middle" className="mb-s">
                <Col flex="none" style={{ width: 85, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <HiOutlineMail style={{ fontSize: 16 }} />
                  Email:
                </Col>

                <Col flex="auto" className="secondary">
                  <TextCopyable>{contact.email}</TextCopyable>
                </Col>
              </Row>

              <Row wrap={false} gutter={[6, 0]} align="middle" className="mb-s">
                <Col flex="none" style={{ width: 85, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <BsTelephone style={{ fontSize: 16 }} />
                  Phone:
                </Col>

                <Col flex="auto" className="secondary">
                  <TextCopyable>{contact.phone}</TextCopyable>
                </Col>
              </Row>

              <Row wrap={false} gutter={[6, 0]} align="middle" className="mb-s">
                <Col flex="none" style={{ width: 85, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <HiOutlineIdentification style={{ fontSize: 16 }} />
                  Job title:
                </Col>

                <Col flex="auto" className="secondary">
                  <TextCopyable>{contact.job_title}</TextCopyable>
                </Col>
              </Row>

              <Row wrap={false} gutter={[6, 0]} align="middle" className="mb-s">
                <Col flex="none" style={{ width: 150, display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                  <GoShieldLock style={{ fontSize: 16, flexShrink: 0 }} />
                  Restricted access:
                </Col>

                <Col flex="auto">
                  <Popconfirm
                    title="Do you want to change strict request access?"
                    visible={pendingValue?.uuid === contact.uuid}
                    onConfirm={async () => {
                      if (!pendingValue) return;
                      const { value } = pendingValue;
                      setUpdatingStrictRequests(contact.uuid);
                      setPendingValue(null);
                      try {
                        await onToggleStrictRequests?.(contact, value);
                        setLocalStrictRequests((prev) => ({ ...prev, [contact.uuid]: value }));
                      } finally {
                        setUpdatingStrictRequests(null);
                      }
                    }}
                    onCancel={() => setPendingValue(null)}
                  >
                    <Switch
                      size="small"
                      checked={localStrictRequests[contact.uuid] ?? false}
                      loading={updatingStrictRequests === contact.uuid}
                      onChange={(value) => setPendingValue({ uuid: contact.uuid, value })}
                    />
                  </Popconfirm>
                </Col>

                <Col flex="none" className="secondary">
                  <Button type="link" size="small" className="primary" onClick={() => onResendInvite(contact)}>
                    Resend
                  </Button>

                  <Popconfirm
                    placement="right"
                    onConfirm={() => onDeleteContactHandler(contact)}
                    title="Do you want to delete this contact?"
                  >
                    <Button size="small" type="link" loading={deletingUuid === contact.uuid} danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Col>
              </Row>

              <Row wrap={false} gutter={[6, 0]} align="middle">
                <Col flex="none" style={{ width: 125, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FiClock style={{ fontSize: 16 }} />
                  Last activity:
                </Col>

                <Col flex="auto" className="secondary">
                  {parseDateToUSFormat(contact.user?.last_activity, true)}
                </Col>
              </Row>
            </div>
          ))}

          {contacts?.length === 0 && <EmptyHorizontal description="There is no contacts" />}
        </Space>
      </Spin>
    </div>
  );
};

export default ContactsCard;
